import React from 'react'
import { Field, Form } from 'react-final-form'
import { useIntl } from 'react-intl'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { thisFieldRequiresResponse } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/messages'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { ClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/Client'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { RadioButtonType } from '@lyrahealth-inc/shared-app-logic/src/models/formMetadata/FormMetadata'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { InputFieldRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/InputField'
import { RadioGroupRFF } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/RadioButtons'
import { PrimaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { useIsEngageDAProvider } from '../../../hooks/useIsEngageDAProvider'
import { episodeStates } from '../../constants/appConstants'

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
}))

const CloseSessionWarningContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['16px'],
}))

const AdjacentButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  marginTop: theme.spacing['8px'],
}))

const StyledCancelButton = styled(SecondaryButton)<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const UpdateEpisodeModal: React.FC<UpdateEpisodeModalProps> = ({
  clientDetails,
  selectedEpisode,
  updateEpisode,
  closeModal,
  isUpdatingEpisode,
  terminationStatuses,
}) => {
  const { formatMessage } = useIntl()
  const { isDAProviderMigrated } = useFlags()

  const { colors } = useTheme()
  const isEngageDAProvider = useIsEngageDAProvider()
  const episodeInProgress = selectedEpisode.state === episodeStates.IN_PROGRESS

  const title = episodeInProgress
    ? isDAProviderMigrated
      ? `Discharge ${clientDetails.first_name}...`
      : `End ${clientDetails.first_name}'s current episode...`
    : `Update ${clientDetails.first_name}'s termination status...`

  return (
    <View>
      <HeaderContainer>
        <Title
          size={TitleSize.LARGE}
          text={title}
          color={colors.textPrimary}
          testID={tID('UpdateEpisodeModal-title')}
        />
      </HeaderContainer>
      <CloseSessionWarningContainer>
        <BodyText
          testID={tID('UpdateEpisodeModal-closeSessionWarning')}
          text={
            isDAProviderMigrated
              ? formatMessage({
                  defaultMessage:
                    'Once you discharge this client, they will be removed from your active client list and you won’t be able to schedule any more sessions. Submit all past session details and invoices, and cancel any upcoming sessions before continuing.',
                  description: 'warning to provider to close all sessions before ending episode',
                })
              : formatMessage({
                  defaultMessage: 'Please make sure you’ve closed all sessions before ending the episode.',
                  description: 'warning to provider to close all sessions before ending episode',
                })
          }
          size={BodyTextSize.DEFAULT}
          color={colors.textError}
        />
      </CloseSessionWarningContainer>
      <Form
        onSubmit={({ endEpisodeReason, endEpisodeComment }) => {
          updateEpisode(endEpisodeReason, endEpisodeComment)
        }}
        render={({ handleSubmit }) => {
          return (
            <>
              <Field
                name='endEpisodeReason'
                component={RadioGroupRFF}
                label={
                  isDAProviderMigrated
                    ? formatMessage(
                        {
                          defaultMessage: `What is the reason for discharging {firstName}?`,
                          description: 'Radio question asking why the provider needs to terminate the episode',
                        },
                        {
                          firstName: clientDetails.first_name,
                        },
                      )
                    : formatMessage({
                        defaultMessage: 'What is the reason?',
                        description: 'Radio question asking why the provider needs to terminate the episode',
                      })
                }
                buttons={terminationStatuses}
                buttonType={RadioButtonType.CIRCLE_TEXT_OUTLINE}
                testID={tID('UpdateEpisodeModal-endEpisodeReason')}
                validate={(value) => (value ? undefined : formatMessage(thisFieldRequiresResponse))}
              />
              <Field
                name='endEpisodeComment'
                component={InputFieldRFF}
                label={formatMessage({
                  defaultMessage: 'Comment',
                  description: 'Text area for comments on why provider is closing session',
                })}
                testID={tID('UpdateEpisodeModal-endEpisodeComment')}
                numberOfLines={3}
                multiline
                scrollEnabled
              />
              <AdjacentButtonContainer>
                <StyledCancelButton
                  text={formatMessage({
                    defaultMessage: 'Cancel',
                    description: 'Button that cancels the process of ending the episode',
                  })}
                  accessibilityLabel={formatMessage({
                    defaultMessage: 'Cancel',
                    description: 'Button that cancels the process of ending the episode',
                  })}
                  onPress={closeModal}
                  testID={tID('UpdateEpisodeModal-cancelButton')}
                />
                <PrimaryButton
                  text={
                    isDAProviderMigrated
                      ? formatMessage({
                          defaultMessage: 'Discharge client',
                          description: 'Button that ends the episode with the client',
                        })
                      : formatMessage({
                          defaultMessage: 'End Episode',
                          description: 'Button that ends the episode with the client',
                        })
                  }
                  accessibilityLabel={
                    isDAProviderMigrated
                      ? formatMessage({
                          defaultMessage: 'Discharge client',
                          description: 'Button that ends the episode with the client',
                        })
                      : formatMessage({
                          defaultMessage: 'End Episode',
                          description: 'Button that ends the episode with the client',
                        })
                  }
                  onPress={() => {
                    if (isDAProviderMigrated || isEngageDAProvider) {
                      track({
                        event: mixpanelEvents.CONFIRM_DISCHARGE_CLIENT,
                        properties: {
                          clientLyraId: clientDetails.id,
                        },
                      })
                    } else {
                      track({
                        event: mixpanelEvents.CONFIRM_END_EPISODE,
                        properties: {
                          clientLyraId: clientDetails.id,
                        },
                      })
                    }
                    handleSubmit()
                  }}
                  loading={isUpdatingEpisode}
                  testID={tID('UpdateEpisodeModal-endEpisodeButton')}
                />
              </AdjacentButtonContainer>
            </>
          )
        }}
      />
    </View>
  )
}

export default UpdateEpisodeModal

type UpdateEpisodeModalProps = {
  clientDetails: ClientObject
  selectedEpisode: Episode
  updateEpisode: (terminationStatus: string, terminationComment: string) => void
  closeModal: () => void
  isUpdatingEpisode: boolean
  terminationStatuses: { label: string; value: string }[]
}
